import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SiteChrome from "@/components/SiteChrome";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NATIONALITIES, flagUrl, nationalityLabel } from "@/lib/nationalities";
import AvatarBuilder from "@/components/AvatarBuilder";
import { AvatarSVG, DEFAULT_AVATAR } from "@/lib/avatarBuilder";
import { Pencil, Play, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { usePlatformMessages } from "@/lib/usePlatformMessages";

const STORAGE = {
  name: "player_name",
  school: "player_school",
  nationality: "player_nationality",
  avatar: "player_avatar_cfg",
  tid: "training_user_id",
};

// Use sessionStorage so player data is cleared on tab/browser close.
const store = {
  get: (k) => sessionStorage.getItem(k),
  set: (k, v) => sessionStorage.setItem(k, v),
  remove: (k) => sessionStorage.removeItem(k),
};

export default function TrainStart() {
  const { t, lang } = useI18n();
  const { m } = usePlatformMessages();
  const nav = useNavigate();
  const [games, setGames] = useState([]);
  const [name, setName] = useState(store.get(STORAGE.name) || "");
  const [school, setSchool] = useState(store.get(STORAGE.school) || "");
  const [nationality, setNationality] = useState(store.get(STORAGE.nationality) || "PT");
  const [avatarCfg, setAvatarCfg] = useState(() => {
    try {
      const raw = store.get(STORAGE.avatar);
      if (raw) return { ...DEFAULT_AVATAR, ...JSON.parse(raw) };
    } catch (_) {}
    return { ...DEFAULT_AVATAR };
  });
  const [gameId, setGameId] = useState("");
  const [level, setLevel] = useState(parseInt(store.get("player_level") || "1", 10));
  // When the game uses tiers, several tiers may share the same `level` number,
  // so we track the user's selection by tier-id instead. For plain-level games
  // this stays empty and `level` (1..5) is used directly.
  const [tierId, setTierId] = useState("");
  const [editingProfile, setEditingProfile] = useState(!store.get(STORAGE.name));
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);

  useEffect(() => {
    api.get(`/games?active_only=true&status=training&lang=${encodeURIComponent(lang || "pt")}`).then((r) => {
      setGames(r.data);
      if (r.data.length) setGameId(r.data[0].id);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // When the selected game changes, reset tier selection: pick the first tier
  // (so the trigger shows ONE tier name instead of every tier sharing that
  // level value). For plain-level games tierId stays empty.
  useEffect(() => {
    const game = games.find((g) => g.id === gameId);
    if (game?.level_system === "tiers" && Array.isArray(game.tiers) && game.tiers.length > 0) {
      const first = game.tiers[0];
      setTierId(first.id);
      setLevel(parseInt(first.level, 10) || 1);
    } else {
      setTierId("");
    }
  }, [gameId, games]);

  const saveProfileAndPlay = async () => {
    if (!name || !name.trim()) {
      toast.error(t("train_name_required"));
      return;
    }
    if (!nationality) {
      toast.error(t("train_nationality_required"));
      return;
    }
    if (!gameId) {
      toast.error(t("train_game_required"));
      return;
    }
    let tid = store.get(STORAGE.tid);
    if (!tid) {
      tid = (await api.post("/training/init")).data.training_user_id;
      store.set(STORAGE.tid, tid);
    }
    // Resolve a unique display name (append "2", "3" if duplicate exists)
    let finalName = name.trim();
    try {
      const r = await api.post("/training/resolve-name", { name: finalName, training_user_id: tid });
      finalName = r.data?.name || finalName;
    } catch (_) {}
    setName(finalName);
    store.set(STORAGE.name, finalName);
    if (school.trim()) {
      store.set(STORAGE.school, school.trim());
    } else {
      // Clear BOTH stores so a lingering value from a previous competition
      // session (CompetitionLobby persists school to localStorage) never
      // leaks into a brand-new training profile that chose not to fill it.
      store.remove(STORAGE.school);
      try { localStorage.removeItem(STORAGE.school); } catch (_) {}
    }
    store.set(STORAGE.nationality, nationality);
    store.set(STORAGE.avatar, JSON.stringify(avatarCfg));
    store.set("player_level", String(level));
    if (tierId) {
      sessionStorage.setItem("player_tier_id", tierId);
    } else {
      sessionStorage.removeItem("player_tier_id");
    }
    nav(`/jogar/${gameId}?mode=training`);
  };

  const hasProfile = !!store.get(STORAGE.name) && !editingProfile;

  return (
    <SiteChrome>
      <div className="max-w-md mx-auto px-6 py-12">
        <h1 className="text-3xl font-[Fredoka] font-semibold text-white mb-2">{t("training")}</h1>
        {t("train_start") && (
          <p className="text-slate-300 mb-6">{t("train_start")}</p>
        )}

        {/* Profile card */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm mb-6" data-testid="profile-card">
          {hasProfile ? (
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowAvatarPicker(true)}
                data-testid="btn-edit-avatar"
                className="relative w-16 h-16 rounded-2xl overflow-hidden border-2 border-slate-200 hover:border-sky-400 transition-colors"
                aria-label="avatar"
              >
                <AvatarSVG cfg={avatarCfg} size={64} />
                <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-sky-500 text-white grid place-items-center text-[10px]"><Pencil className="w-3 h-3" /></span>
              </button>
              <div className="flex-1">
                <div className="font-[Fredoka] text-xl font-bold text-slate-900">{name}</div>
                {school && (
                  <div className="text-sm text-slate-600 mt-0.5 truncate" data-testid="profile-school">{school}</div>
                )}
                <div className="flex items-center gap-2 text-sm text-slate-600 mt-1">
                  {flagUrl(nationality) && <img src={flagUrl(nationality)} alt={nationality} className="w-5 h-3.5 rounded-sm object-cover" />}
                  <span>{nationalityLabel(nationality)}</span>
                </div>
              </div>
              <button
                data-testid="btn-edit-profile"
                onClick={() => setEditingProfile(true)}
                className="text-xs font-bold text-slate-500 hover:text-sky-600 underline"
              >
                Editar
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowAvatarPicker(true)}
                  data-testid="btn-pick-avatar"
                  className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-dashed border-slate-300 hover:border-sky-400"
                >
                  <AvatarSVG cfg={avatarCfg} size={64} />
                </button>
                <div className="flex-1">
                  <Label>{t("your_name")}</Label>
                  <Input data-testid="train-name" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
              </div>
              <div>
                <Label>{t("school")} <span className="text-slate-400 text-xs">({t("optional")})</span></Label>
                <Input
                  data-testid="train-school"
                  value={school}
                  onChange={(e) => setSchool(e.target.value)}
                />
              </div>
              <div>
                <Label>{t("your_nationality")}</Label>
                <Select value={nationality} onValueChange={setNationality}>
                  <SelectTrigger data-testid="train-nationality"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {NATIONALITIES.map((n) => (
                      <SelectItem key={n.code} value={n.code} data-testid={`nat-${n.code}`}>
                        <span className="inline-flex items-center gap-2">
                          {flagUrl(n.code) && <img src={flagUrl(n.code)} alt={n.code} className="w-5 h-3.5 rounded-sm object-cover" />}
                          {n.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {/* Game selector */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
          <div>
            <Label>{t("select_game")}</Label>
            <Select value={gameId} onValueChange={setGameId}>
              <SelectTrigger data-testid="train-game"><SelectValue /></SelectTrigger>
              <SelectContent>
                {games.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{(() => {
              const game = games.find((g) => g.id === gameId);
              return game?.level_system === "tiers" ? t("level_or_tier") : t("level");
            })()}</Label>
            {(() => {
              const game = games.find((g) => g.id === gameId);
              const useTiers = game?.level_system === "tiers" && Array.isArray(game.tiers) && game.tiers.length > 0;
              if (useTiers) {
                return (
                  <Select
                    value={tierId}
                    onValueChange={(id) => {
                      const tr = game.tiers.find((x) => x.id === id);
                      if (tr) { setTierId(id); setLevel(parseInt(tr.level, 10) || 1); }
                    }}
                  >
                    <SelectTrigger data-testid="train-level"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {game.tiers.map((tier) => (
                        <SelectItem key={tier.id} value={tier.id} data-testid={`tier-${tier.id}`}>
                          {tier.name} <span className="text-slate-400 text-xs">({t("level_paren")} {tier.level})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }
              return (
                <Select value={String(level)} onValueChange={(v) => setLevel(parseInt(v, 10))}>
                  <SelectTrigger data-testid="train-level"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(game?.format === "quiz" ? [1, 2, 3, 4] : [1, 2, 3, 4, 5]).map((n) => (
                      <SelectItem key={n} value={String(n)} data-testid={`level-${n}`}>Nível {n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            })()}
          </div>
          <Button
            data-testid="btn-start-training"
            className="w-full bg-sky-500 hover:bg-sky-600 rounded-full font-bold py-6"
            onClick={saveProfileAndPlay}
          >
            <Play className="w-4 h-4 mr-2" /> {t("start")}
          </Button>

          {/* Ranking VS TEMPO — links to the public rankings page,
              pre-filtered to the currently-selected game and level/tier. */}
          <Button
            data-testid="btn-view-training-ranking"
            variant="outline"
            className="w-full mt-3 rounded-full font-bold border-2 border-sky-300 text-sky-700 hover:bg-sky-50 py-5"
            onClick={() => {
              const params = new URLSearchParams();
              params.set("mode", "training");
              if (gameId) params.set("game", gameId);
              if (level) params.set("level", String(level));
              nav(`/resultados?${params.toString()}`);
            }}
            disabled={!gameId}
          >
            <BarChart3 className="w-4 h-4 mr-2" /> {t("view_ranking_vs_time")}
          </Button>
        </div>
      </div>

      {showAvatarPicker && (
        <AvatarBuilder
          current={avatarCfg}
          trainingUserId={store.get(STORAGE.tid) || undefined}
          onSave={(cfg) => {
            setAvatarCfg(cfg);
            store.set(STORAGE.avatar, JSON.stringify(cfg));
            const tid = store.get(STORAGE.tid);
            if (tid) {
              api.put(`/training-users/${tid}/avatar`, { avatar_cfg: cfg }).catch((err) => { console.error("[training-users.avatar]", err); });
            }
            setShowAvatarPicker(false);
          }}
          onClose={() => setShowAvatarPicker(false)}
        />
      )}
    </SiteChrome>
  );
}
